import axiosInstance, {
  checkSession,
  logout as axiosLogout,
} from '@/utils/axiosInstance'
import { store } from '@/store/index'

export default {
  namespaced: true,
  state: {
    loggined: false,
    address: '',
    erc20Address: '',
    erc721Address: '',
    erc1155Address: '',
    chainID: 1,
    contractList: [],
    contractAddress: '',
    // 역할 기반 권한 관리
    groups: [],
    roles: [],
    permissions: [],
  },
  getters: {
    isLoggined: (state) => {
      return state.loggined
    },
    address: (state) => {
      return state.address
    },
    erc20Address: (state) => {
      return state.erc20Address
    },
    erc721Address: (state) => {
      return state.erc721Address
    },
    erc1155Address: (state) => {
      return state.erc1155Address
    },
    chainID: (state) => {
      return state.chainID
    },
    contractList: (state) => {
      return state.contractList
    },
    contractAddress: (state) => {
      return state.contractAddress
    },
    // 그룹/역할/권한 관련 getters
    groups: (state) => state.groups,
    roles: (state) => state.roles,
    permissions: (state) => state.permissions,
    hasGroup: (state) => (groupName) => state.groups.includes(groupName),
    hasRole: (state) => (roleName) => state.roles.includes(roleName),
    hasPermission: (state) => (route) => state.permissions.includes(route),
    isAdmin: (state) =>
      state.roles.includes('SUPER_ADMIN') || state.roles.includes('ADMIN'),
    isSuperAdmin: (state) => state.roles.includes('SUPER_ADMIN'),
  },
  mutations: {
    setLoginStatus(state, loggined) {
      state.loggined = loggined
    },
    setAddress(state, address) {
      state.address = address
    },
    setContractList(state, contractList) {
      state.contractList = JSON.parse(JSON.stringify(contractList))
    },
    setErc20Address(state, erc20Address) {
      state.erc20Address = erc20Address
    },
    setErc721Address(state, erc721Address) {
      state.erc721Address = erc721Address
    },
    setErc1155Address(state, erc1155Address) {
      state.erc1155Address = erc1155Address
    },
    setChainID(state, chainID) {
      state.chainID = chainID
    },
    setContractAddress(state, contractAddress) {
      state.contractAddress = contractAddress
    },
    // 그룹/역할/권한 관련 mutations
    setGroups(state, groups) {
      state.groups = groups || []
    },
    setRoles(state, roles) {
      state.roles = roles || []
    },
    setPermissions(state, permissions) {
      state.permissions = permissions || []
    },
    clearGroupsRolesAndPermissions(state) {
      state.groups = []
      state.roles = []
      state.permissions = []
    },
  },
  actions: {
    /**
     * Login action - sends signature to server
     * Server will set HttpOnly cookie with session ID
     */
    async login(context, { address, message, signature, callback }) {
      try {
        const result = await axiosInstance.post('/signIn', {
          address,
          message,
          signature,
        })

        if (result.status === 200) {
          context.commit('setAddress', address)
          context.commit('setLoginStatus', true)

          if (result.data.groups) {
            context.commit('setGroups', result.data.groups)
          }
          if (result.data.roles) {
            context.commit('setRoles', result.data.roles)
          }
          if (result.data.permissions) {
            context.commit('setPermissions', result.data.permissions)
          }

          if (callback) callback(true)
        }
      } catch (_err) {
        if (callback) callback(false)
        store.dispatch(
          'toast/error',
          'You are unable to log in. Please verify your account credentials.'
        )
      }
    },

    /**
     * Logout action - clears session on server
     */
    async logout(context) {
      await axiosLogout()

      context.commit('setAddress', '')
      context.commit('setContractList', [])
      context.commit('setErc20Address', '')
      context.commit('setErc721Address', '')
      context.commit('setErc1155Address', '')
      context.commit('setChainID', 0)
      context.commit('setContractAddress', '')
      context.commit('setLoginStatus', false)
      context.commit('clearGroupsRolesAndPermissions')
    },

    /**
     * Check login status from server session
     */
    async checkLoginStatus(context) {
      const sessionData = await checkSession()

      if (sessionData) {
        context.commit('setLoginStatus', true)
        context.commit('setAddress', sessionData.address)
        context.commit('setGroups', sessionData.groups)
        context.commit('setRoles', sessionData.roles)
        context.commit('setPermissions', sessionData.permissions)

        // Load contract list
        try {
          const r = await axiosInstance.post('/contract/list', {
            address: sessionData.address,
            offset: 0,
            limit: 1000,
          })

          if (r.status == 200) {
            const list = r.data.list
            const contractList = r.data.contractList.filter((item) =>
              list.includes(item.contractAddress)
            )

            context.commit('setContractList', contractList)
            const erc20List = contractList.filter((item) =>
              item.tokenType == 'erc20' ? true : false
            )
            const erc721List = contractList.filter((item) =>
              item.tokenType == 'erc721' ? true : false
            )
            const erc1155List = contractList.filter((item) =>
              item.tokenType == 'erc1155' ? true : false
            )
            if (erc20List.length > 0) {
              context.commit('setErc20Address', erc20List[0].contractAddress)
              context.commit('setChainID', erc20List[0].chainID)
              context.commit('setContractAddress', erc20List[0].contractAddress)
            }
            if (erc721List.length > 0) {
              context.commit('setErc721Address', erc721List[0].contractAddress)
            }
            if (erc1155List.length > 0) {
              context.commit(
                'setErc1155Address',
                erc1155List[0].contractAddress
              )
            }
          }
        } catch (error) {}
      } else {
        context.commit('setLoginStatus', false)
        context.commit('setAddress', '')
        context.commit('setContractList', [])
        context.commit('setErc20Address', '')
        context.commit('setErc721Address', '')
        context.commit('setErc1155Address', '')
        context.commit('setChainID', 0)
        context.commit('setContractAddress', '')
        context.commit('clearGroupsRolesAndPermissions')
      }
    },

    setContractList(context, contractList) {
      context.commit('setContractList', contractList)
    },
    setErc20Address(context, erc20address) {
      context.commit('setErc20Address', erc20address)
    },
    setErc721Address(context, erc721address) {
      context.commit('setErc721Address', erc721address)
    },
    setErc1155Address(context, erc1155address) {
      context.commit('setErc1155Address', erc1155address)
    },
    setChainID(context, chainID) {
      context.commit('setChainID', chainID)
    },
    setContractAddress(context, contractAddress) {
      context.commit('setContractAddress', contractAddress)
    },
  },
}
